import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, Loader } from 'lucide-react'
import { useGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import type { PersonalDocumentEmployeeResponse } from '@/api/model'
import { Card } from '@/components/shadcn/card'
import { Button } from '@/components/shadcn/button'
import { EmployeeCard } from './EmployeeCard'
import { FilterSearch } from '@/components/common/filters/FilterSearch'

interface Props {
  serviceModuleId: number
  onView: (employee: PersonalDocumentEmployeeResponse) => void
  /** File selection for exactly this employee (assignment explicit, no personnel number needed). */
  onUpload: (files: FileList, employee: PersonalDocumentEmployeeResponse) => void
  onOpenLatest: () => void
}

/**
 * Employee list card: loads the employees itself, filters client-side by
 * search and renders an `EmployeeCard` per employee.
 */
export function EmployeeDocumentsCard({ serviceModuleId, onView, onUpload, onOpenLatest }: Props) {
  const { t } = useTranslation('service-modules-personal-documents')
  const [search, setSearch] = useState('')
  const { data: response, isLoading, isError } =
    useGetApiCompaniesCurrentServicemodulesPersonaldocumentsServiceModuleIdEmployees(serviceModuleId)

  const filtered = useMemo(() => {
    // The generated hooks return the full Axios response — the payload lives under `.data`.
    const employees = ((response as any)?.data ?? []) as PersonalDocumentEmployeeResponse[]
    return employees.filter((e) =>
      `${e.firstName ?? ''} ${e.lastName ?? ''}`.toLowerCase().includes(search.toLowerCase()),
    )
  }, [response, search])

  return (
    <Card className="gap-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-semibold">{t('personalDocuments.listTitle')}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={onOpenLatest}>
            <Eye className="size-4" />
            {t('personalDocuments.recentlyUploaded')}
          </Button>
          <FilterSearch
            searchLabel={t('personalDocuments.searchPlaceholder')}
            search={search}
            onChange={(value) => setSearch(value ?? '')}
            useDebounce={false}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
          <Loader className="size-5 animate-spin text-primary" />
          <span className="text-sm">{t('personalDocuments.loading')}</span>
        </div>
      ) : isError ? (
        <p className="py-10 text-center text-sm text-muted-foreground">{t('personalDocuments.listFailed')}</p>
      ) : filtered.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">{t('personalDocuments.listEmpty')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {filtered.map((employee) => (
            <EmployeeCard
              key={employee.employeeId ?? `${employee.firstName}-${employee.lastName}`}
              employee={employee}
              onView={onView}
              onUpload={onUpload}
            />
          ))}
        </div>
      )}
    </Card>
  )
}
